/**
 * Validación en frontera del API de contenidos.
 *
 * Mismo criterio que la capa de Red de Centros: estricto con lo imprescindible
 * (un registro sin `id` o sin título se descarta, porque no se podría ni
 * enlazar ni pintar) y tolerante con lo accesorio, que se normaliza.
 *
 * Si el backend cambia un campo, el fallo salta aquí —con un mensaje que dice
 * qué campo es— en vez de reventar dentro de un componente.
 */

import { ApiValidationError } from '../errors';
import type {
  HeroSlideDto,
  ImagenDto,
  NoticiaDto,
  NoticiaStatusDto,
  SlideButtonDto,
  SlideStatusDto,
} from './dto';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const recortado = value.trim();
    return recortado === '' ? null : recortado;
  }
  // Los `id` pueden llegar numéricos aunque el contrato pida cadena.
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

const asNumber = (value: unknown, porDefecto: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parseado = Number(value);
    if (Number.isFinite(parseado)) return parseado;
  }
  return porDefecto;
};

const requireText = (origen: Record<string, unknown>, campo: string, contexto: string): string => {
  const valor = asText(origen[campo]);
  if (valor === null) {
    throw new ApiValidationError(`${contexto}: falta el campo obligatorio "${campo}".`);
  }
  return valor;
};

const asRecord = (value: unknown, contexto: string): Record<string, unknown> => {
  if (!isRecord(value)) throw new ApiValidationError(`${contexto}: se esperaba un objeto.`);
  return value;
};

/**
 * Saca la lista de la respuesta.
 *
 * Acepta tanto un array pelado como los envoltorios habituales (`data`,
 * `items`, `results`), para no depender de cómo decida devolverlo el backend.
 */
const asList = (value: unknown, contexto: string): unknown[] => {
  if (Array.isArray(value)) return value;

  if (isRecord(value)) {
    for (const clave of ['data', 'items', 'results'] as const) {
      const anidado: unknown = value[clave];
      if (Array.isArray(anidado)) return anidado;
    }
  }

  throw new ApiValidationError(`${contexto}: se esperaba una lista.`);
};

const parseImagen = (value: unknown): ImagenDto | null => {
  if (!isRecord(value)) return null;
  const url = asText(value['url']);
  if (url === null) return null;
  return { url, fileName: asText(value['fileName']) ?? '' };
};

const ESTADOS_SLIDE: readonly SlideStatusDto[] = ['draft', 'published'];

/* ------------------------------------------------------------------ noticias */

/**
 * `NoticiaResponse.status` llega como cadena. Valores reales comprobados a
 * mano contra el backend de dev: "Pendiente", "Publicado", "Archivado" (se
 * comparan en minúsculas). El resto de claves son alias defensivos por si el
 * backend cambia de idioma o de forma; cualquier cosa fuera del mapa cae en
 * `pending` con un aviso por consola.
 */
const ESTADOS_NOTICIA_CONOCIDOS: Readonly<Record<string, NoticiaStatusDto>> = {
  pendiente: 'pending',
  pending: 'pending',
  draft: 'pending',
  borrador: 'pending',
  publicado: 'published',
  publicada: 'published',
  published: 'published',
  archivado: 'archived',
  archivada: 'archived',
  archived: 'archived',
};

function parseEstadoNoticia(valor: string | null, contexto: string): NoticiaStatusDto {
  if (valor === null) return 'pending';

  const normalizado = ESTADOS_NOTICIA_CONOCIDOS[valor.toLowerCase()];
  if (normalizado) return normalizado;

  console.warn(
    `${contexto}: estado de noticia no reconocido ("${valor}"). Se trata como "pending". ` +
      'Revisa el mapa ESTADOS_NOTICIA_CONOCIDOS en parsers.ts.',
  );
  return 'pending';
}

export function parseNoticia(value: unknown, contexto = 'Noticia'): NoticiaDto {
  const origen = asRecord(value, contexto);

  return {
    id: requireText(origen, 'id', contexto),
    title: requireText(origen, 'title', contexto),
    slug: asText(origen['slug']) ?? '',
    excerpt: asText(origen['excerpt']) ?? '',
    content: asText(origen['content']) ?? '',
    image: parseImagen(origen['image']),
    author: asText(origen['author']) ?? '',
    status: parseEstadoNoticia(asText(origen['status']), contexto),
    featured: origen['featured'] === true,
    createdAt: asText(origen['createdAt']) ?? new Date(0).toISOString(),
    publishedAt: asText(origen['publishedAt']),
    externalUrl: asText(origen['externalUrl']),
  };
}

/**
 * Saca la lista de noticias de la respuesta.
 *
 * `asList` ya entiende el envoltorio `{ items: [...] }` de
 * `SearchResultOfNoticiaResponse`, así que sirve tanto para `search-publicadas`
 * como para un array pelado.
 */
export function parseNoticias(value: unknown): NoticiaDto[] {
  return asList(value, 'Listado de noticias').map((item, indice) =>
    parseNoticia(item, `Noticia #${indice + 1}`),
  );
}

/* -------------------------------------------------------------------- slides */

function parseBoton(value: unknown, contexto: string): SlideButtonDto | null {
  if (!isRecord(value)) return null;

  const label = asText(value['label']);
  const href = asText(value['href']);
  if (label === null || href === null) return null;

  return {
    id: asText(value['id']) ?? `${contexto}-${label}`,
    label,
    href,
    variant: value['variant'] === 'secondary' ? 'secondary' : 'primary',
  };
}

export function parseHeroSlide(value: unknown, contexto = 'Slide'): HeroSlideDto {
  const origen = asRecord(value, contexto);
  const estado = asText(origen['status']);
  const botones: unknown = origen['buttons'];

  return {
    id: requireText(origen, 'id', contexto),
    title: requireText(origen, 'title', contexto),
    subtitle: asText(origen['subtitle']) ?? '',
    image: parseImagen(origen['image']),
    order: asNumber(origen['order'], 0),
    // Igual que en noticias: ante la duda, no se publica.
    status: ESTADOS_SLIDE.find((e) => e === estado) ?? 'draft',
    imageOffsetX: asNumber(origen['imageOffsetX'], 0),
    imageOffsetY: asNumber(origen['imageOffsetY'], 0),
    imageScale: asNumber(origen['imageScale'], 1),
    buttons: (Array.isArray(botones) ? botones : [])
      .map((boton, indice) => parseBoton(boton, `${contexto}-${indice}`))
      .filter((boton): boton is SlideButtonDto => boton !== null),
    createdAt: asText(origen['createdAt']) ?? new Date(0).toISOString(),
    updatedAt: asText(origen['updatedAt']) ?? new Date(0).toISOString(),
  };
}

export function parseHeroSlides(value: unknown): HeroSlideDto[] {
  return asList(value, 'Listado de slides').map((item, indice) =>
    parseHeroSlide(item, `Slide #${indice + 1}`),
  );
}
